/**
 * Academy lessons: a scripted scenario and a list of steps, each checked against the sim.
 * No DOM here; the briefing and coach panels show the text, and the tests replay every step's
 * solution so lessons stay passable as the sim changes.
 */
import type { AirportData, RealismLevel, RealismOptions } from '@atc/core';
import type { RadioMessage, Sim, SimEvent } from '@atc/sim';

/** Where a lesson sits in the training route. The Academy screen groups by this. */
export type LessonTier = 'beginner' | 'mid' | 'pro';

/** What the player reads before the sim starts: the situation, and what passing looks like. */
export interface LessonBriefing {
  /** "Ben Gurion approach · landing 12" — station and configuration for this exercise. */
  situation: string;
  /** The traffic picture and why it matters, one paragraph per entry. */
  scene: string[];
  /** What passing looks like, one line each. */
  objectives: string[];
  /** Standing constraints that hold for the whole exercise. */
  notes: string[];
  /** Official airport charts shown first in the briefing, in display order. */
  chartIds: string[];
}

export interface StepContext {
  sim: Sim;
  airport: AirportData;
  /** Player interactions since this step began. */
  selections?: readonly string[];
  cardCommands?: readonly string[];
  /** Sim events since the step started. */
  events: readonly SimEvent[];
  /**
   * Radio traffic since the step started. Radar contact, stand by and say again change no flight
   * state and emit no SimEvent, so a step that teaches them can only be checked from the log.
   */
  radio: readonly RadioMessage[];
  /**
   * Every event since the lesson began. A player who works ahead can finish the lesson's last
   * objective during an early step, so terminal outcomes — a handoff, a landing, an aircraft
   * removed — are read from here rather than from the per-step buffer, which the step boundary
   * clears.
   */
  all: readonly SimEvent[];
  /** Sim seconds since the step started. */
  elapsedS: number;
}

/**
 * 'pass' moves on, { fail } ends the attempt with an explanation, null keeps waiting.
 * { pass } also moves on, and carries a line for the coach: the step's point was already settled,
 * usually because the player did the job in a different order. Working ahead is not a mistake, so
 * it is never a fail — but the coach still says why the step is being skipped.
 */
export type StepResult = 'pass' | { pass: string } | { fail: string } | null;

export interface LessonStep {
  title: string;
  /** What to do and why. */
  text: string;
  /** What to decide, shown at the first help level. */
  hint: string;
  /** The call as it is spoken on the radio, shown at the second help level. */
  say: string;
  /** Commands that complete the step, entered at its start; "Show me" types them, tests replay them. */
  solution: string[];
  /** Run once as the step begins, for the few steps that need to arm something in the sim. */
  onEnter?(sim: Sim): void;
  check(ctx: StepContext): StepResult;
  /** Sim seconds the tests allow after the solution; players have no time limit. */
  testTimeoutS?: number;
}

export interface LessonScript {
  /** Place the lesson's aircraft; the sim starts empty with no spawning. */
  setup(sim: Sim): void;
  steps: LessonStep[];
}

export interface Lesson {
  position?: 'tower' | 'approach' | 'legacy';
  id: string;
  title: string;
  summary: string;
  /** Lower-case ICAO code. */
  airport: string;
  tier: LessonTier;
  briefing: LessonBriefing;
  /** COMMAND_CATALOG ids this lesson practises; the help drawer lists them as what can be said. */
  commands: string[];
  /** The realism the lesson was written and tested at; a player's own setting is used in the game. */
  realism: RealismLevel;
  /**
   * Realism flags the checks depend on, forced whatever the player has chosen. Wake separation is
   * off at low realism, so a wake lesson would otherwise be unpassable for a player who sets low.
   * Merged over the player's level, which still reports as their own.
   */
  realismOverrides?: Partial<RealismOptions>;
  /** A scripted lesson may use a runway configuration different from the airport default. */
  runways?: { arrivals: string[]; departures: string[] };
  /** Garble the first readback of each clearance, for the lessons that teach catching one. */
  trainingReadback?: boolean;
  build(airport: AirportData): LessonScript;
}

export type RunStatus = 'running' | 'passed' | 'failed' | 'complete';

export interface LessonRun {
  readonly script: LessonScript;
  readonly stepIndex: number;
  readonly step: LessonStep | undefined;
  readonly status: RunStatus;
  readonly failText: string;
  /** Set with a passed step the player had already settled; empty for an ordinary pass. */
  readonly passNote: string;
  /** Check the current step; call after each sim tick. */
  update(): RunStatus;
  select(callsign: string): void;
  cardCommand(command: string): void;
  /** After a pass, start the next step (or complete the lesson). */
  next(): void;
  dispose(): void;
}

/** Sim options a lesson needs: an empty sky and no traffic generation. */
export const LESSON_SIM_OPTIONS = { initialTraffic: false, arrivalsPerHour: 0, departuresPerHour: 0, pilotPatience: false, fuel: false, emergencies: 'off' } as const;

export function startLesson(lesson: Lesson, sim: Sim, airport: AirportData): LessonRun {
  const script = lesson.build(airport);
  if (lesson.trainingReadback) sim.enableTrainingReadback();
  script.setup(sim);
  let stepIndex = 0;
  let status: RunStatus = script.steps.length ? 'running' : 'complete';
  let failText = '';
  let passNote = '';
  let events: SimEvent[] = [];
  const all: SimEvent[] = [];
  let radio: RadioMessage[] = [];
  let selections: string[] = [];
  let cardCommands: string[] = [];
  let startS = sim.simTimeS;
  // Attached after setup, so traffic the script places is not mistaken for something the player did.
  const off = sim.on('event', (e) => { events.push(e); all.push(e); });
  const offRadio = sim.on('radio', (m) => { radio.push(m); });
  script.steps[0]?.onEnter?.(sim);

  return {
    script,
    get stepIndex() { return stepIndex; },
    get step() { return script.steps[stepIndex]; },
    get status() { return status; },
    get failText() { return failText; },
    get passNote() { return passNote; },
    update() {
      const step = script.steps[stepIndex];
      if (status !== 'running' || !step) return status;
      const impact = all.find((event) => event.type === 'collision');
      if (impact?.type === 'collision') {
        status = 'failed';
        failText = `${impact.a.callsign} and ${impact.b.callsign} collided. Review the traffic picture, then retry.`;
        return status;
      }
      const r = step.check({ sim, airport, events, radio, all, selections, cardCommands, elapsedS: sim.simTimeS - startS });
      if (r === 'pass') status = 'passed';
      else if (r && 'pass' in r) { status = 'passed'; passNote = r.pass; }
      else if (r) { status = 'failed'; failText = r.fail; }
      return status;
    },
    select(callsign) { if (status === 'running') selections.push(callsign); },
    cardCommand(command) { if (status === 'running') cardCommands.push(command); },
    next() {
      if (status !== 'passed') return;
      stepIndex++;
      events = [];
      radio = [];
      selections = [];
      cardCommands = [];
      passNote = '';
      startS = sim.simTimeS;
      status = stepIndex >= script.steps.length ? 'complete' : 'running';
      if (status === 'running') script.steps[stepIndex]?.onEnter?.(sim);
    },
    dispose() { off(); offRadio(); },
  };
}
